import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Image, Upload, Trash2, Copy, Search, Grid, List,
  RefreshCw, X, Check, ExternalLink, Info
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { API, axios, showToast, copyToClipboard } from './adminHelpers';

const EXISTING_ASSETS = [
  { id: 'logo-short', name: '13cabsbook Logo (Short)', url: 'https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/zydgwvt1_13cabsbook%20logo%20short.png', type: 'image/png', category: 'logos' },
  { id: 'logo-full', name: '13cabsbook Logo (Full)', url: 'https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/kqniqnnb_13cabsbook%20logo%20full.png', type: 'image/png', category: 'logos' },
  { id: 'fleet-sedan', name: 'Sedan Taxi', url: 'https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/q4ct6q3p_image.png', type: 'image/png', category: 'fleet' },
  { id: 'fleet-premium-sedan', name: 'Premium Sedan (Mercedes)', url: 'https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/24mvvbgz_image.png', type: 'image/png', category: 'fleet' },
  { id: 'fleet-suv', name: 'SUV Taxi', url: 'https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/pov05qzs_image.png', type: 'image/png', category: 'fleet' },
  { id: 'fleet-premium-suv', name: 'Premium SUV (Lexus)', url: 'https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/kh7gk9ht_lexus-suv-1.jpg', type: 'image/jpg', category: 'fleet' },
  { id: 'fleet-maxi', name: 'Maxi Taxi', url: 'https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/8ly5dt5z_image.png', type: 'image/png', category: 'fleet' },
  { id: 'payment-banner', name: 'Payment Methods Banner', url: 'https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/ymhjcz2n_IMG_2740.jpg', type: 'image/jpg', category: 'branding' },
  { id: 'hero-bg', name: 'Hero Background', url: 'https://images.unsplash.com/photo-1552912809-9e8ea7ddc3d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920', type: 'image/jpg', category: 'backgrounds' },
];

export const MediaLibrary = ({ authHeader }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [uploading, setUploading] = useState(false);
  const [showUrlUpload, setShowUrlUpload] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [altInput, setAltInput] = useState('');
  const fileInputRef = useRef(null);

  const fetchMedia = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/media`, { headers: authHeader });
      const dbMedia = res.data || [];
      // Merge existing assets with DB media, avoid duplicates
      const dbUrls = new Set(dbMedia.map(m => m.url));
      const existingNotInDb = EXISTING_ASSETS.filter(a => !dbUrls.has(a.url));
      setMedia([...dbMedia, ...existingNotInDb]);
    } catch {
      // If API doesn't exist yet, just show existing assets
      setMedia(EXISTING_ASSETS);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const handleUrlUpload = async () => {
    if (!urlInput.trim()) { showToast('URL is required', 'error'); return; }
    setUploading(true);
    try {
      const mediaItem = {
        name: nameInput || urlInput.split('/').pop() || 'Untitled',
        url: urlInput.trim(),
        alt_text: altInput || '',
        type: 'image/url',
        category: 'uploads',
      };
      await axios.post(`${API}/admin/media`, mediaItem, { headers: authHeader });
      showToast('Media added!');
      setUrlInput('');
      setNameInput('');
      setAltInput('');
      setShowUrlUpload(false);
      fetchMedia();
    } catch {
      showToast('Failed to add media', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast('File too large (max 5MB)', 'error');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/media/upload`, formData, {
        headers: { ...authHeader, 'Content-Type': 'multipart/form-data' },
      });
      showToast('File uploaded!');
      fetchMedia();
    } catch {
      showToast('Upload failed. Try adding via URL instead.', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!window.confirm('Delete this media item?')) return;
    try {
      await axios.delete(`${API}/admin/media/${mediaId}`, { headers: authHeader });
      showToast('Media deleted');
      fetchMedia();
      if (selectedMedia?.id === mediaId) setSelectedMedia(null);
    } catch {
      showToast('Failed to delete', 'error');
    }
  };

  const categories = ['all', ...new Set(media.map(m => m.category).filter(Boolean))];

  const filteredMedia = media.filter(m => {
    const matchesSearch = !searchQuery || m.name?.toLowerCase().includes(searchQuery.toLowerCase()) || m.url?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || m.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6" data-testid="media-library">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Media Library</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowUrlUpload(!showUrlUpload)} data-testid="add-url-btn">
            <ExternalLink className="w-4 h-4 mr-2" /> Add URL
          </Button>
          <Button className="bg-primary hover:bg-primary-hover" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-file-btn">
            <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Uploading...' : 'Upload File'}
          </Button>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
        </div>
      </div>

      {showUrlUpload && (
        <div className="bg-white rounded-lg shadow p-6" data-testid="url-upload-form">
          <h3 className="font-semibold mb-4">Add Media from URL</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Image URL *</Label>
              <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://example.com/image.jpg" className="mt-1" data-testid="media-url-input" />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={nameInput} onChange={(e) => setNameInput(e.target.value)} placeholder="Image name" className="mt-1" data-testid="media-name-input" />
            </div>
            <div>
              <Label>Alt Text</Label>
              <Input value={altInput} onChange={(e) => setAltInput(e.target.value)} placeholder="Description for accessibility" className="mt-1" data-testid="media-alt-input" />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={handleUrlUpload} disabled={uploading} className="bg-primary hover:bg-primary-hover" data-testid="submit-url-btn">
              {uploading ? 'Adding...' : 'Add Media'}
            </Button>
            <Button variant="outline" onClick={() => setShowUrlUpload(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search media..." className="pl-10" data-testid="media-search" />
        </div>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="media-category-filter">
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
          ))}
        </select>
        <div className="flex border rounded-lg overflow-hidden">
          <button onClick={() => setViewMode('grid')} className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}><Grid className="w-4 h-4" /></button>
          <button onClick={() => setViewMode('list')} className={`px-3 py-2 ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}><List className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${selectedMedia ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
          {loading ? (
            <div className="bg-white rounded-lg p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
          ) : filteredMedia.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center text-gray-500">No media found.</div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {filteredMedia.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedMedia(item)}
                  className={`group relative bg-white rounded-lg shadow-sm overflow-hidden cursor-pointer border-2 transition-all hover:shadow-md ${selectedMedia?.id === item.id ? 'border-primary ring-2 ring-primary/20' : 'border-transparent'}`}
                  data-testid={`media-item-${item.id}`}
                >
                  <div className="aspect-square bg-gray-100">
                    <img src={item.url} alt={item.alt_text || item.name} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.src = ''; e.target.alt = 'Failed to load'; }} />
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium text-gray-700 truncate">{item.name}</p>
                    {item.category && <span className="text-[10px] text-gray-400">{item.category}</span>}
                  </div>
                  {selectedMedia?.id === item.id && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden divide-y">
              {filteredMedia.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedMedia(item)}
                  className={`flex items-center gap-4 p-3 cursor-pointer hover:bg-gray-50 ${selectedMedia?.id === item.id ? 'bg-blue-50' : ''}`}
                  data-testid={`media-item-${item.id}`}
                >
                  <img src={item.url} alt={item.name} className="w-16 h-16 object-cover rounded" loading="lazy" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{item.name}</p>
                    <p className="text-xs text-gray-400 truncate">{item.url}</p>
                  </div>
                  {item.category && <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">{item.category}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedMedia && (
          <div className="bg-white rounded-lg shadow p-6" data-testid="media-detail-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Media Details</h3>
              <button onClick={() => setSelectedMedia(null)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <img src={selectedMedia.url} alt={selectedMedia.alt_text || selectedMedia.name} className="w-full rounded-lg mb-4 max-h-60 object-contain bg-gray-50" />
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500">Name</Label>
                <p className="font-medium text-sm">{selectedMedia.name}</p>
              </div>
              {selectedMedia.alt_text && (
                <div>
                  <Label className="text-xs text-gray-500">Alt Text</Label>
                  <p className="text-sm">{selectedMedia.alt_text}</p>
                </div>
              )}
              {selectedMedia.category && (
                <div>
                  <Label className="text-xs text-gray-500">Category</Label>
                  <p className="text-sm">{selectedMedia.category}</p>
                </div>
              )}
              <div>
                <Label className="text-xs text-gray-500">URL</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input value={selectedMedia.url} readOnly className="text-xs font-mono" data-testid="media-url-display" />
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(selectedMedia.url, 'URL')} data-testid="copy-media-url">
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4 pt-4 border-t">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => copyToClipboard(`<img src="${selectedMedia.url}" alt="${selectedMedia.alt_text || selectedMedia.name}" />`, 'HTML tag')} data-testid="copy-html-tag">
                Copy HTML
              </Button>
              {selectedMedia.category !== 'logos' && selectedMedia.category !== 'fleet' && selectedMedia.category !== 'branding' && selectedMedia.category !== 'backgrounds' && (
                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteMedia(selectedMedia.id)} data-testid="delete-media-btn">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="text-xs text-gray-400 flex items-center gap-1">
        <Info className="w-3 h-3" /> {filteredMedia.length} items - Built-in assets (logos, fleet, branding) cannot be deleted
      </div>
    </div>
  );
};
